import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getCourseDetailById, getCourseLessons } from '../../api/courses';
import { SEO } from '../../components/SEO';
import { CustomVideoPlayer } from './CustomVideoPlayer';
import './CoursePlayer.scss';

export const CoursePlayer = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((s) => s.auth || {});

  const [courseData, setCourseData] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const queryParams = new URLSearchParams(location.search);
  const lessonIdParam = queryParams.get('lessonId');
  
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [openModules, setOpenModules] = useState(['mod-1']);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fallback fake data if course not found or we don't have ID, to show exact UI matching the image
        const cData = { title: 'English Learning Videos', category_name: 'Education' };
        setCourseData(cData);
        
        // Mock static modules specifically structured like the image
        const mockModules = [
          {
            id: 'mod-1', title: 'Module 1: Getting Started',
            completedCount: 3, totalCount: 8,
            lessons: [
              { id: 'l1', num: '1.', title: 'Self-Introduction', duration: '08:45', status: 'active' },
              { id: 'l2', num: '2.', title: 'Greetings and Basic Introductions', duration: '09:18', status: 'completed' },
              { id: 'l3', num: '3.', title: 'Talking About Yourself', duration: '09:18', status: 'circle' },
              { id: 'l4', num: '4.', title: 'Common Phrases', duration: '06:45', status: 'circle' },
              { id: 'l5', num: '5.', title: 'Listening Practice 1', duration: '08:20', status: 'locked' },
              { id: 'l6', num: '6.', title: 'Pronunciation Basics', duration: '10:15', status: 'locked' },
              { id: 'l7', num: '7.', title: 'Quick Quiz 1', duration: '04:30', status: 'locked' },
              { id: 'l8', num: '8.', title: 'Module 1 Review', duration: '08:30', status: 'locked' },
            ]
          },
          { 
            id: 'mod-2', title: 'Module 2: Everyday English', 
            completedCount: 0, totalCount: 8, 
            lessons: [
              { id: 'm2-1', num: '1.', title: 'Ordering Food', duration: '10:00', status: 'locked' },
              { id: 'm2-2', num: '2.', title: 'Asking for Directions', duration: '08:30', status: 'locked' },
              { id: 'm2-3', num: '3.', title: 'Shopping Vocab', duration: '12:15', status: 'locked' },
            ] 
          },
          { 
            id: 'mod-3', title: 'Module 3: Speaking Practice', 
            completedCount: 0, totalCount: 8, 
            lessons: [
              { id: 'm3-1', num: '1.', title: 'Roleplay: At the Bank', duration: '15:20', status: 'locked' },
            ] 
          },
          { id: 'mod-4', title: 'Module 4: Real-Life Conversations', completedCount: 0, totalCount: 8, lessons: [] },
          { id: 'mod-5', title: 'Module 5: Advanced Topics', completedCount: 0, totalCount: 8, lessons: [] },
        ];
        
        setLessons(mockModules);
        setSelectedLesson(mockModules[0].lessons[0]);
        
      } catch (err) {
        console.error("Failed to load course player data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, lessonIdParam]);

  if (loading || !courseData) {
    return <div className="course-player-page justify-content-center align-items-center bg-dark text-white d-flex"><div className="spinner-border text-yellow"></div></div>;
  }

  const categoryName = courseData.category_name || 'Education';
  const lessonTitle = selectedLesson ? `${selectedLesson.num} ${selectedLesson.title}` : '1. Self-Introduction';

  const toggleModule = (modId) => {
    setOpenModules(prev => 
      prev.includes(modId) ? prev.filter(id => id !== modId) : [...prev, modId]
    );
  };

  return (
    <div className="course-player-page">
      <SEO title={lessonTitle} />
      
      {/* Top Navigation */}
      <div className="player-top-nav">
        <div className="breadcrumbs">
          <Link to="/courses">Courses</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/courses">{categoryName}</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to={`/course-detail/${id}`}>{courseData.title}</Link>
          <i className="bi bi-chevron-right"></i>
          <span className="active">{lessonTitle}</span>
        </div>
        <div className="top-actions">
          <button className="btn-nav-action"><i className="bi bi-file-earmark-text"></i> Notes</button>
          <button className="btn-nav-action"><i className="bi bi-arrows-angle-expand"></i> Fullscreen</button>
          <button className="btn-nav-action" onClick={() => navigate(`/course-detail/${id}`)}><i className="bi bi-box-arrow-right"></i> Exit Course</button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="player-main-content">
        <Row className="g-4 m-0">
          
          {/* Left Column - Player & Details */}
          <Col xl={8} lg={8} className="p-0 pe-xl-3">
            
            {/* Video Container */}
            <div className="video-container">
              {/* Dummy Image for screenshot exact match */}
              <div style={{ position: 'relative', width: '100%', height: '100%', backgroundImage: 'url("https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)' }}></div>
                {/* Custom player controls placeholder matching image */}
                <div style={{ position: 'absolute', bottom: '1rem', left: '1rem', right: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', color: 'white' }}>
                  <i className="bi bi-play-fill fs-4"></i>
                  <i className="bi bi-arrow-counterclockwise fs-5" style={{position: 'relative'}}><span style={{position: 'absolute', fontSize: '0.45rem', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 'bold'}}>10</span></i>
                  <i className="bi bi-skip-end-fill fs-5"></i>
                  <i className="bi bi-volume-up-fill fs-5"></i>
                  <span style={{ fontSize: '0.8rem' }}>02:31 / 08:45</span>
                  <div style={{ flexGrow: 1, height: '4px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px', position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: '30%', background: '#4f46e5', borderRadius: '2px' }}>
                      <div style={{ position: 'absolute', right: '-4px', top: '-4px', width: '12px', height: '12px', background: 'white', borderRadius: '50%' }}></div>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.8rem' }}>1.25x</span>
                  <i className="bi bi-badge-cc fs-5"></i>
                  <i className="bi bi-gear-fill fs-5"></i>
                  <i className="bi bi-fullscreen fs-5"></i>
                </div>
              </div>
            </div>

            {/* Lesson Header */}
            <div className="lesson-header">
              <div className="title-area">
                <h2>{lessonTitle}</h2>
                <div className="lesson-meta">
                  <div className="meta-item"><i className="bi bi-clock"></i> 08:45 min</div>
                  <div className="meta-item"><i className="bi bi-camera-video"></i> Video Lesson</div>
                  <div className="meta-item"><i className="bi bi-reception-4"></i> Beginner</div>
                </div>
              </div>
              <div className="lesson-actions">
                <button className="btn-action btn-mark-done">
                  <i className="bi bi-check-lg"></i> Mark as Done
                </button>
                <button className="btn-action btn-outline">
                  <i className="bi bi-bookmark"></i> Add to Watch Later
                </button>
                <button className="btn-action btn-outline">
                  <i className="bi bi-share"></i> Share Lesson
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="custom-tabs-container">
              <div className="custom-tabs">
                <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                <button className={`tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>Q&A (24)</button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <Row className="g-4 m-0">
                <Col md={7} className="p-0 pe-md-2">
                  <div className="info-card">
                    <h5>About This Lesson</h5>
                    <p>In this lesson, you'll learn how to introduce yourself in English with confidence. We'll go over common expressions, examples, and a simple practice to help you speak naturally.</p>
                    
                    <Row className="mt-4 m-0">
                      <Col sm={6} className="p-0 pe-2">
                        <h6>What You'll Learn</h6>
                        <ul className="skills-list">
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> How to introduce yourself in English</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Common phrases and expressions</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Real-life examples</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Practice speaking with confidence</li>
                        </ul>
                      </Col>
                      <Col sm={6} className="p-0 ps-2">
                        <h6>Skills You'll Gain</h6>
                        <ul className="skills-list">
                          <li><i className="bi bi-person-video icon-plain"></i> Speaking Confidence</li>
                          <li><i className="bi bi-chat-square-text icon-plain"></i> Self-Expression</li>
                          <li><i className="bi bi-headphones icon-plain"></i> Listening Comprehension</li>
                          <li><i className="bi bi-journal-text icon-plain"></i> Vocabulary Building</li>
                        </ul>
                      </Col>
                    </Row>
                  </div>
                </Col>
                
                <Col md={5} className="p-0 ps-md-2">
                  <div className="info-card mb-3 pb-2 pt-3">
                    <h5 className="mb-2">Lesson Resources</h5>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Lesson Transcript (PDF)</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Vocabulary List</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Practice Worksheet</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item border-0">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Extra Examples</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                  </div>
                  
                  <div className="info-card pb-3 pt-3">
                    <h6 className="mb-3">Lesson Tags</h6>
                    <div className="tags-container">
                      <span className="tag-pill">Self-Introduction</span>
                      <span className="tag-pill">Speaking</span>
                      <span className="tag-pill">Beginner</span>
                      <span className="tag-pill">Vocabulary</span>
                      <span className="tag-pill">Confidence</span>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {activeTab === 'resources' && (
              <div className="info-card">
                <h5 className="mb-4">All Course Resources</h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-pdf-fill" style={{color: '#ef4444'}}></i>
                        <div>
                          <div className="r-name">Module 1 Full Guide</div>
                          <div className="r-type">PDF Document • 2.4 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-zip-fill" style={{color: '#ffc107'}}></i>
                        <div>
                          <div className="r-name">Practice Audio Files</div>
                          <div className="r-type">ZIP Archive • 15 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-pdf-fill" style={{color: '#ef4444'}}></i>
                        <div>
                          <div className="r-name">Conversation Cheat Sheet</div>
                          <div className="r-type">PDF Document • 1.1 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-link-45deg" style={{color: '#3b82f6'}}></i>
                        <div>
                          <div className="r-name">External Tool Reference</div>
                          <div className="r-type">Web Link</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-box-arrow-up-right"></i></button>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === 'qa' && (
              <div className="info-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Questions & Answers (24)</h5>
                  <button className="btn btn-primary" style={{backgroundColor: '#4f46e5', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600}}>
                    Ask a new question
                  </button>
                </div>
                
                <div className="qa-search mb-4 position-relative">
                  <i className="bi bi-search position-absolute" style={{top: '50%', transform: 'translateY(-50%)', left: '1rem', color: '#9ca3af'}}></i>
                  <input type="text" placeholder="Search for a question..." className="form-control" style={{backgroundColor: '#1f2937', border: '1px solid #374151', color: 'white', paddingLeft: '2.5rem', borderRadius: '8px'}} />
                </div>

                <div className="qa-list d-flex flex-column gap-3">
                  {/* QA Item 1 */}
                  <div className="qa-item p-3 border rounded" style={{borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)'}}>
                    <div className="d-flex gap-3 mb-2">
                      <div style={{width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>JS</div>
                      <div>
                        <div className="fw-bold" style={{fontSize: '0.9rem'}}>John Smith</div>
                        <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>2 days ago</div>
                      </div>
                    </div>
                    <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>How do I know when to use formal vs informal greetings?</div>
                    <p style={{fontSize: '0.85rem', color: '#d1d5db', marginBottom: '0.75rem'}}>I always get confused when speaking to someone older than me but in a casual setting.</p>
                    <div className="d-flex align-items-center gap-3" style={{fontSize: '0.8rem', color: '#9ca3af'}}>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-hand-thumbs-up"></i> 12</span>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-chat-dots"></i> 3 Replies</span>
                    </div>
                  </div>

                  {/* QA Item 2 */}
                  <div className="qa-item p-3 border rounded" style={{borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)'}}>
                    <div className="d-flex gap-3 mb-2">
                      <div style={{width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>ML</div>
                      <div>
                        <div className="fw-bold" style={{fontSize: '0.9rem'}}>Maria Lopez</div>
                        <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>5 days ago</div>
                      </div>
                    </div>
                    <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>Is there a transcript for this video?</div>
                    <p style={{fontSize: '0.85rem', color: '#d1d5db', marginBottom: '0.75rem'}}>I learn better when I can read along.</p>
                    <div className="d-flex align-items-center gap-3" style={{fontSize: '0.8rem', color: '#9ca3af'}}>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-hand-thumbs-up"></i> 5</span>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-chat-dots"></i> 1 Reply</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </Col>

          {/* Right Column - Curriculum */}
          <Col xl={4} lg={4} className="p-0 ps-xl-3">
            <div className="sidebar-container">
              
              {/* Progress Card */}
              <div className="progress-card">
                <div className="progress-header">
                  <span>Course Progress</span>
                  <span>30% Complete</span>
                </div>
                <div className="progress-bar-bg">
                  <div className="progress-bar-fill" style={{ width: '30%' }}></div>
                </div>
                <div className="progress-meta">
                  48 Lessons • 12h 30m
                </div>
              </div>

              {/* Modules List */}
              <div className="modules-list">
                {lessons.map((mod) => {
                  const isOpen = openModules.includes(mod.id);
                  return (
                    <div className="module-card" key={mod.id}>
                      <div className="module-header" onClick={() => toggleModule(mod.id)}>
                        <div className="mod-title">{mod.title}</div>
                        <div className="mod-meta">
                          <span>{mod.completedCount}/{mod.totalCount}</span>
                          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
                        </div>
                      </div>
                      
                      {isOpen && mod.lessons && (
                        <div className="module-lessons">
                          {mod.lessons.map((lesson) => {
                            const isActive = lesson.status === 'active';
                            return (
                              <div 
                                key={lesson.id} 
                                className={`lesson-item ${isActive ? 'active' : ''}`}
                                onClick={() => setSelectedLesson(lesson)}
                              >
                                <div className="lesson-title-area">
                                  <span className="lesson-num">{lesson.num}</span>
                                  {isActive && <i className="bi bi-play-circle-fill lesson-play-icon"></i>}
                                  <span className="lesson-title">{lesson.title}</span>
                                </div>
                                <div className="lesson-right">
                                  <span className="lesson-duration">{lesson.duration}</span>
                                  {lesson.status === 'completed' && <i className="bi bi-check-circle lesson-status-icon completed"></i>}
                                  {lesson.status === 'circle' && <i className="bi bi-circle lesson-status-icon"></i>}
                                  {lesson.status === 'locked' && <i className="bi bi-lock lesson-status-icon"></i>}
                                  {lesson.status === 'active' && <i className="bi bi-play-circle-fill lesson-status-icon" style={{opacity:0}}></i> /* hidden placeholder */}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </Col>
        </Row>
      </div>
      
      {/* Full Width Bottom Navigation */}
      <div className="bottom-nav-container">
        <div className="nav-side">
          <Link to="#" className="nav-btn-circle"><i className="bi bi-chevron-left"></i></Link>
          <div>
            <div className="nav-text">Previous Lesson</div>
            <div className="nav-title">Greetings and Basic Introductions</div>
            <div className="nav-text mt-1 d-flex align-items-center gap-1">09:18 <i className="bi bi-check-circle status-icon"></i></div>
          </div>
        </div>
        
        <div className="nav-center">
          <div className="progress-msg">Great job! You're making progress.</div>
          <button className="btn-next-main">Next Lesson <i className="bi bi-chevron-right ms-2 fs-5"></i></button>
        </div>
        
        <div className="nav-side next">
          <div>
            <div className="nav-text">Next Lesson</div>
            <div className="nav-title">Talking About Yourself</div>
            <div className="nav-text mt-1">09:18</div>
          </div>
          <Link to="#" className="nav-btn-circle"><i className="bi bi-chevron-right"></i></Link>
        </div>
      </div>

    </div>
  );
};
